import { keyBy } from 'lodash-es'
import { useMemo } from 'react'

import { useProduct } from '../application/product/productContext'
import { ExcessLine, Quote } from '../domain'
import { ExcessCombination } from '../state/configuration'

interface Excesses {
  main: ExcessLine[]
  other: ExcessLine[]
  voluntary: Array<{
    voluntaryExcessCode: string
    excesses?: number[]
    excess: number
  }>
  coverLevels?: Record<string, ExcessCombination[]>
}

export const isVoluntaryExcess =
  (voluntaryExcessCode: string) => (voluntary: Excesses['voluntary'][0]) =>
    voluntary.voluntaryExcessCode === voluntaryExcessCode

export const useExcesses = (quote: Quote | undefined): Excesses => {
  const {
    product: { excess },
  } = useProduct()

  return useMemo(() => {
    const excessesByCategory = keyBy<ExcessLine>(
      quote?.excessLines ?? [],
      'excessCategory',
    )

    const main = excess?.mainExcesses
      ?.map((it) => excessesByCategory[it])
      ?.filter(Boolean)
      ?.filter((it) => it.mandatoryExcess > 0)

    const other = excess?.otherExcesses
      ?.map((it) => excessesByCategory[it])
      ?.filter(Boolean)

    const voluntary = excess?.voluntaryExcesses?.map(
      ({ voluntaryExcessCode, excesses }) =>
        ({
          voluntaryExcessCode,
          excesses,
          excess: quote?.voluntaryExcesses[voluntaryExcessCode] ?? 0,
        } satisfies Excesses['voluntary'][0]),
    )

    return {
      main: main ?? [],
      other: other ?? [],
      voluntary: voluntary ?? [],
      coverLevels: excess?.coverLevels ?? {},
    }
  }, [excess, quote])
}
